import React, { useEffect, useRef, useState } from 'react';
import { portfolioData } from '../data/mock';
import { Play } from 'lucide-react';

const PortfolioSection = () => {
  const [isVisible, setIsVisible] = useState(false);
  const [isHovered, setIsHovered] = useState(null);
  const [isPaused, setIsPaused] = useState(false);
  const [isDragging, setIsDragging] = useState(false);
  const [startX, setStartX] = useState(0);
  const [scrollLeft, setScrollLeft] = useState(0);
  const sectionRef = useRef(null);
  const scrollRef = useRef(null);
  const animationRef = useRef(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
        }
      },
      { threshold: 0.2 }
    );

    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }

    return () => observer.disconnect();
  }, []);

  // Auto-scroll using JavaScript
  useEffect(() => {
    const scrollContainer = scrollRef.current;
    if (!scrollContainer) return;

    const autoScroll = () => {
      if (!isPaused && !isDragging && scrollContainer) {
        scrollContainer.scrollLeft += 1;
        
        // Reset to start when reaching the end (for seamless loop)
        const maxScroll = scrollContainer.scrollWidth - scrollContainer.clientWidth;
        if (scrollContainer.scrollLeft >= maxScroll - 1) {
          scrollContainer.scrollLeft = 0;
        }
      }
      animationRef.current = requestAnimationFrame(autoScroll);
    };

    animationRef.current = requestAnimationFrame(autoScroll);

    return () => {
      if (animationRef.current) {
        cancelAnimationFrame(animationRef.current);
      }
    };
  }, [isPaused, isDragging]);

  // Drag handlers
  const handleMouseDown = (e) => {
    setIsDragging(true);
    setStartX(e.pageX - scrollRef.current.offsetLeft);
    setScrollLeft(scrollRef.current.scrollLeft);
  };

  const handleMouseUp = () => {
    setIsDragging(false);
  };

  const handleMouseLeave = () => {
    setIsDragging(false);
    setIsPaused(false);
  };

  const handleMouseMove = (e) => {
    if (!isDragging) return;
    e.preventDefault();
    const x = e.pageX - scrollRef.current.offsetLeft;
    const walk = (x - startX) * 2; // Scroll speed multiplier
    scrollRef.current.scrollLeft = scrollLeft - walk;
  };

  // Touch handlers for mobile
  const handleTouchStart = (e) => {
    setIsDragging(true);
    setStartX(e.touches[0].pageX - scrollRef.current.offsetLeft);
    setScrollLeft(scrollRef.current.scrollLeft);
  };

  const handleTouchEnd = () => {
    setIsDragging(false);
  };

  const handleTouchMove = (e) => {
    if (!isDragging) return;
    const x = e.touches[0].pageX - scrollRef.current.offsetLeft;
    const walk = (x - startX) * 2;
    scrollRef.current.scrollLeft = scrollLeft - walk;
  };

  // Duplicate data for seamless infinite scroll
  const duplicatedPodcasts = [...portfolioData, ...portfolioData, ...portfolioData];

  return (
    <section ref={sectionRef} id="work" className="py-24 bg-[#04101C] relative overflow-hidden">
      {/* Background */}
      <div className="absolute inset-0">
        <div className="absolute top-1/3 left-1/4 w-[400px] h-[400px] bg-[#00C4CC]/10 rounded-full blur-[150px]" />
        <div className="absolute bottom-1/3 right-1/4 w-[300px] h-[300px] bg-[#00E08F]/10 rounded-full blur-[120px]" />
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        {/* Section Header */}
        <div className="mb-12">
          <h2
            className={`text-3xl md:text-4xl font-bold text-[#F5FDFF] mb-4 transition-all duration-700 ${
              isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
            }`}
          >
            Our <span className="bg-gradient-to-r from-[#00C4CC] via-[#00E08F] to-[#FFF7AE] bg-clip-text text-transparent">Work</span>
          </h2>
          <p
            className={`text-[#F5FDFF]/60 max-w-xl transition-all duration-700 delay-200 ${
              isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
            }`}
          >
            Podcasts we&apos;ve brought to life. From concept to chart-topper.
          </p>
        </div>

        {/* Auto-scrolling Carousel with Drag */}
        <div className="relative overflow-hidden">
          {/* Gradient Fade Left */}
          <div className="absolute left-0 top-0 bottom-0 w-16 bg-gradient-to-r from-[#04101C] to-transparent z-10 pointer-events-none" />
          
          {/* Gradient Fade Right */}
          <div className="absolute right-0 top-0 bottom-0 w-16 bg-gradient-to-l from-[#04101C] to-transparent z-10 pointer-events-none" />

          <div
            ref={scrollRef}
            className={`flex gap-6 pb-4 overflow-x-auto scrollbar-hide select-none ${isDragging ? 'cursor-grabbing' : 'cursor-grab'}`}
            onMouseEnter={() => setIsPaused(true)}
            onMouseLeave={handleMouseLeave}
            onMouseDown={handleMouseDown}
            onMouseUp={handleMouseUp}
            onMouseMove={handleMouseMove}
            onTouchStart={handleTouchStart}
            onTouchEnd={handleTouchEnd}
            onTouchMove={handleTouchMove}
          >
            {duplicatedPodcasts.map((podcast, index) => {
              const CardWrapper = podcast.link ? 'a' : 'div';
              const cardProps = podcast.link && !isDragging ? { href: podcast.link, target: '_blank', rel: 'noopener noreferrer' } : {};
              
              return (
                <CardWrapper
                  key={`${podcast.id}-${index}`}
                  {...cardProps}
                  className={`flex-shrink-0 w-72 group cursor-pointer transition-all duration-700 ${
                    isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-12'
                  }`}
                  onMouseEnter={() => setIsHovered(`${podcast.id}-${index}`)}
                  onMouseLeave={() => setIsHovered(null)}
                  onClick={(e) => isDragging && e.preventDefault()}
                  draggable={false}
                >
                  {/* Cover Image */}
                  <div className="relative aspect-square rounded-2xl overflow-hidden mb-4">
                    <img
                      src={podcast.image}
                      alt={podcast.title}
                      className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110 pointer-events-none"
                      draggable={false}
                    />

                    {/* Gradient Border on Hover */}
                    <div
                      className={`absolute inset-0 rounded-2xl border-4 transition-all duration-300 ${
                        isHovered === `${podcast.id}-${index}`
                          ? 'border-[#00C4CC] shadow-[0_0_30px_rgba(0,196,204,0.5)]'
                          : 'border-transparent'
                      }`}
                    />

                    {/* Play Overlay */}
                    <div
                      className={`absolute inset-0 bg-[#04101C]/60 flex items-center justify-center transition-opacity duration-300 ${
                        isHovered === `${podcast.id}-${index}` ? 'opacity-100' : 'opacity-0'
                      }`}
                    >
                      <div className="w-16 h-16 rounded-full bg-gradient-to-r from-[#00C4CC] to-[#00E08F] flex items-center justify-center hover:scale-110 transition-transform duration-300">
                        <Play size={28} className="text-[#04101C] fill-current ml-1" />
                      </div>
                    </div>

                    {/* Category Badge */}
                    <div className="absolute top-3 left-3 px-3 py-1 rounded-full bg-[#04101C]/70 backdrop-blur-sm">
                      <span className="text-xs font-medium text-[#00E08F]">{podcast.category}</span>
                    </div>
                  </div>

                  {/* Info */}
                  <h3 className="text-lg font-bold text-[#F5FDFF] mb-1 group-hover:text-[#00E08F] transition-colors duration-300">
                    {podcast.title}
                  </h3>
                  <p className="text-[#F5FDFF]/60 text-sm">by {podcast.host}</p>
                </CardWrapper>
              );
            })}
          </div>
        </div>
      </div>

      <style>{`
        .scrollbar-hide::-webkit-scrollbar {
          display: none;
        }
        .scrollbar-hide {
          -ms-overflow-style: none;
          scrollbar-width: none;
        }
      `}</style>
    </section>
  );
};

export default PortfolioSection;
